import React, { useEffect, useState, useRef, Suspense, lazy } from 'react';
import './components/PreloadAssets';
import { MetaPixel } from './components/MetaPixel';
import { Hero } from './components/sections/Hero';
import { SocialProofBar } from './components/sections/SocialProofBar';
// Below-the-fold sections lazy-loaded.
// Split into TWO stages so initial JS budget on mobile stays minimal:
//   Stage A (near-fold): hydrates as soon as user scrolls past hero
//   Stage B (far-fold): hydrates only when user scrolls within ~1500px of it
const TopReviews = lazy(() => import('./components/sections/TopReviews').then(m => ({
  default: m.TopReviews
})));
const Agitation = lazy(() => import('./components/sections/Agitation').then(m => ({
  default: m.Agitation
})));
const BeforeAfter = lazy(() => import('./components/sections/BeforeAfter').then(m => ({
  default: m.BeforeAfter
})));
const WhoFor = lazy(() => import('./components/sections/WhoFor').then(m => ({
  default: m.WhoFor
})));
const WhyDifferent = lazy(() => import('./components/sections/WhyDifferent').then(m => ({
  default: m.WhyDifferent
})));
const SixBooks = lazy(() => import('./components/sections/SixBooks').then(m => ({
  default: m.SixBooks
})));
const ContentPreview = lazy(() => import('./components/sections/ContentPreview').then(m => ({
  default: m.ContentPreview
})));
const Bonuses = lazy(() => import('./components/sections/Bonuses').then(m => ({
  default: m.Bonuses
})));
const Offer = lazy(() => import('./components/sections/Offer').then(m => ({
  default: m.Offer
})));
const Guarantee = lazy(() => import('./components/sections/Guarantee').then(m => ({
  default: m.Guarantee
})));
const Mission = lazy(() => import('./components/sections/Mission').then(m => ({
  default: m.Mission
})));
const SocialFeed = lazy(() => import('./components/sections/SocialFeed').then(m => ({
  default: m.SocialFeed
})));
const FAQ = lazy(() => import('./components/sections/FAQ').then(m => ({
  default: m.FAQ
})));
const Numbers = lazy(() => import('./components/sections/Numbers').then(m => ({
  default: m.Numbers
})));
const FinalCTA = lazy(() => import('./components/sections/FinalCTA').then(m => ({
  default: m.FinalCTA
})));
const Footer = lazy(() => import('./components/sections/Footer').then(m => ({
  default: m.Footer
})));
const SectionFallback = () => <div className="w-full h-64" aria-hidden="true" data-id="element-0" />;
/**
 * Defers mounting children until either:
 *   - the user scrolls within `rootMargin` of the placeholder, OR
 *   - the browser is idle (so fast scrollers don't see blank space)
 *
 * Massive mobile win: stops React from loading & rendering all 14 below-fold
 * sections during the initial paint, freeing up the main thread for the hero.
 */
function DeferUntilNear({
  children,
  rootMargin = '600px',
  idleMs = 2500
}: {
  children: React.ReactNode;
  rootMargin?: string;
  idleMs?: number;
}) {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (show) return;
    let cancelled = false;
    // Fallback: hydrate on idle so users who don't scroll still get the content
    const idleId = window.setTimeout(() => {
      if (!cancelled) setShow(true);
    }, idleMs);
    if (typeof IntersectionObserver !== 'undefined' && ref.current) {
      const io = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
          window.clearTimeout(idleId);
        }
      }, {
        rootMargin
      });
      io.observe(ref.current);
      return () => {
        cancelled = true;
        window.clearTimeout(idleId);
        io.disconnect();
      };
    }
    return () => {
      cancelled = true;
      window.clearTimeout(idleId);
    };
  }, [show, rootMargin, idleMs]);
  return <div ref={ref} style={{
    minHeight: show ? undefined : 1
  }} data-id="element-1">
      {show ? children : null}
    </div>;
}
export function App() {
  return <div className="w-full min-h-screen" data-id="element-2">
      <MetaPixel data-id="element-3" />
      <Hero data-id="element-4" />
      <SocialProofBar data-id="element-5" />

      {/* Near-fold: loads as soon as user scrolls past hero */}
      <Suspense fallback={<SectionFallback data-id="element-7" />} data-id="element-6">
        <DeferUntilNear rootMargin="400px" idleMs={1500} data-id="element-8">
          <TopReviews data-id="element-9" />
          <Agitation data-id="element-10" />
          <BeforeAfter data-id="element-11" />
        </DeferUntilNear>

        {/* Mid-page: loads after a short delay */}
        <DeferUntilNear rootMargin="600px" idleMs={2500} data-id="element-12">
          <WhoFor data-id="element-13" />
          <WhyDifferent data-id="element-14" />
          <SixBooks data-id="element-15" />
          <ContentPreview data-id="element-16" />
        </DeferUntilNear>

        {/* Far-fold: loads only when user gets close */}
        <DeferUntilNear rootMargin="800px" idleMs={4000} data-id="element-17">
          <Bonuses data-id="element-18" />
          <Offer data-id="element-19" />
          <Guarantee data-id="element-20" />
          <Mission data-id="element-21" />
        </DeferUntilNear>

        {/* Bottom: lowest priority */}
        <DeferUntilNear rootMargin="800px" idleMs={6000} data-id="element-22">
          <SocialFeed data-id="element-23" />
          <FAQ data-id="element-24" />
          <Numbers data-id="element-25" />
          <FinalCTA data-id="element-26" />
          <Footer data-id="element-27" />
        </DeferUntilNear>
      </Suspense>
    </div>;
}